/**
 * Pure functional API client
 * All functions return promises, no side effects
 */

import { trackApiError } from '../errorTracking';

const API_BASE = '/api';

// ============================================================================
// API CALL DEBUGGING TOOL
// ============================================================================
// Enable with: localStorage.setItem('debugApiCalls', 'true')
// Disable with: localStorage.removeItem('debugApiCalls')
// ============================================================================

const apiCallTracker = {
  enabled: false,
  calls: [], // { endpoint, method, caller, timestamp }
  lastReportTime: Date.now(),
  intervalId: null,

  // Extract caller from stack trace
  getCaller() {
    const stack = new Error().stack;
    if (!stack) return 'unknown';

    const lines = stack.split('\n');
    // Skip first 3 lines (Error, getCaller, trackCall)
    for (let i = 4; i < Math.min(lines.length, 10); i++) {
      const line = lines[i];
      // Look for lines with file paths
      if (line.includes('src/app/')) {
        // Extract file path and line number
        const match = line.match(/src\/app\/(.*?)\.jsx?:(\d+)/);
        if (match) {
          return `${match[1]}:${match[2]}`;
        }
      }
    }
    return 'unknown';
  },

  // Track an API call
  trackCall(endpoint, method = 'GET') {
    if (!this.enabled) return;

    const caller = this.getCaller();
    this.calls.push({
      endpoint,
      method,
      caller,
      timestamp: Date.now()
    });
  },

  // Generate report
  generateReport() {
    if (!this.enabled || this.calls.length === 0) return;

    const now = Date.now();
    const timeSinceLastReport = (now - this.lastReportTime) / 1000;

    // Split calls into new (since last report) and all
    const newCalls = this.calls.filter(c => c.timestamp > this.lastReportTime);
    const allCalls = this.calls;

    // Overall summary
    console.group(`🔍 API Call Debug Report (${timeSinceLastReport.toFixed(1)}s)`);
    console.log(`%c📊 OVERALL: ${newCalls.length} new | ${allCalls.length} total`,
      'font-weight: bold; font-size: 14px; color: #2563eb');

    // Group by endpoint
    const byEndpoint = {};
    allCalls.forEach(call => {
      const key = `${call.method} ${call.endpoint}`;
      if (!byEndpoint[key]) {
        byEndpoint[key] = { new: 0, total: 0 };
      }
      byEndpoint[key].total++;
      if (call.timestamp > this.lastReportTime) {
        byEndpoint[key].new++;
      }
    });

    // Sort by total count (descending)
    const sortedEndpoints = Object.entries(byEndpoint)
      .sort((a, b) => b[1].total - a[1].total);

    console.group(`📍 BY ENDPOINT (${sortedEndpoints.length} unique)`);
    sortedEndpoints.forEach(([endpoint, counts]) => {
      const style = counts.new > 0
        ? 'color: #dc2626; font-weight: bold'
        : 'color: #6b7280';
      console.log(`%c${endpoint.padEnd(60)} +${counts.new.toString().padStart(3)} | ${counts.total.toString().padStart(4)} total`, style);
    });
    console.groupEnd();

    // Group by endpoint + caller
    const byEndpointCaller = {};
    allCalls.forEach(call => {
      const key = `${call.method} ${call.endpoint} ← ${call.caller}`;
      if (!byEndpointCaller[key]) {
        byEndpointCaller[key] = { new: 0, total: 0 };
      }
      byEndpointCaller[key].total++;
      if (call.timestamp > this.lastReportTime) {
        byEndpointCaller[key].new++;
      }
    });

    // Sort by total count (descending)
    const sortedCallers = Object.entries(byEndpointCaller)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 20); // Show top 20

    console.group(`📞 BY ENDPOINT + CALLER (top 20)`);
    sortedCallers.forEach(([key, counts]) => {
      const style = counts.new > 0
        ? 'color: #dc2626; font-weight: bold'
        : 'color: #6b7280';
      console.log(`%c${key.padEnd(100)} +${counts.new.toString().padStart(3)} | ${counts.total.toString().padStart(4)} total`, style);
    });
    console.groupEnd();

    // Highlight hot spots (endpoints with > 10 new calls)
    const hotSpots = sortedEndpoints.filter(([_, counts]) => counts.new > 10);
    if (hotSpots.length > 0) {
      console.group(`🔥 HOT SPOTS (>${10} new calls)`);
      hotSpots.forEach(([endpoint, counts]) => {
        console.log(`%c⚠️  ${endpoint}: +${counts.new} calls in ${timeSinceLastReport.toFixed(1)}s`,
          'color: #dc2626; font-weight: bold; font-size: 12px');
      });
      console.groupEnd();
    }

    console.groupEnd();

    this.lastReportTime = now;
  },

  // Start tracking
  start() {
    if (this.intervalId) return; // Already started

    this.enabled = true;
    this.calls = [];
    this.lastReportTime = Date.now();

    console.log('%c🔍 API Call Tracking ENABLED (reporting every 3s)',
      'background: #2563eb; color: white; padding: 4px 8px; font-weight: bold');
    console.log('%cDisable with: localStorage.removeItem("debugApiCalls")',
      'color: #6b7280; font-style: italic');

    this.intervalId = setInterval(() => this.generateReport(), 3000);
  },

  // Stop tracking
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.enabled = false;

    console.log('%c🔍 API Call Tracking DISABLED',
      'background: #dc2626; color: white; padding: 4px 8px; font-weight: bold');
  },

  // Reset counters
  reset() {
    this.calls = [];
    this.lastReportTime = Date.now();
    console.log('%c🔍 API Call Tracking RESET',
      'background: #16a34a; color: white; padding: 4px 8px; font-weight: bold');
  }
};

// Check if debugging is enabled in localStorage
if (typeof window !== 'undefined') {
  const debugEnabled = localStorage.getItem('debugApiCalls') === 'true';
  if (debugEnabled) {
    apiCallTracker.start();
  }

  // Expose to window for easy control
  window.apiDebug = {
    start: () => {
      localStorage.setItem('debugApiCalls', 'true');
      apiCallTracker.start();
    },
    stop: () => {
      localStorage.removeItem('debugApiCalls');
      apiCallTracker.stop();
    },
    reset: () => apiCallTracker.reset(),
    report: () => apiCallTracker.generateReport()
  };
}

/**
 * Determines if an API error should be tracked
 * Some errors are expected and shouldn't pollute analytics
 */
const shouldTrackError = (endpoint, status) => {
  // 404s for word lookups are expected (not all words are in DB/KG)
  if (status === 404 && (endpoint.includes('/words/') || endpoint.includes('/kg/wordform'))) {
    return false;
  }

  // Add other expected errors here as needed

  return true;
};

/**
 * Pure fetch wrapper with error handling
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE}${endpoint}`;

  // Track API call for debugging
  const method = options.method || 'GET';
  apiCallTracker.trackCall(endpoint, method);

  // Get auth token from either localStorage or sessionStorage
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
    credentials: 'include', // For session cookies
  };

  const finalOptions = { ...defaultOptions, ...options };

  try {
    const response = await fetch(url, finalOptions);

    if (!response.ok) {
      const error = new Error(`API Error: ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.response = response;

      // Only track unexpected errors
      if (shouldTrackError(endpoint, response.status)) {
        trackApiError(endpoint, finalOptions, error);
      }

      throw error;
    }

    // Return raw response for non-JSON responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response;
  } catch (error) {
    // Track network errors (timeouts, connection failures, etc.)
    if (!error.status) {
      trackApiError(endpoint, finalOptions, {
        ...error,
        status: 0,
        statusText: 'Network Error'
      });
    }

    // Re-throw to preserve error flow
    throw error;
  }
};

/**
 * Pure API method helpers
 */
export const api = {
  get: (endpoint, params) => {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return apiFetch(`${endpoint}${query}`);
  },

  post: (endpoint, data) => 
    apiFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: (endpoint, data) =>
    apiFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: (endpoint, data) =>
    apiFetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (endpoint) =>
    apiFetch(endpoint, {
      method: 'DELETE',
    }),
};

/**
 * Pure function to build query strings
 */
export const buildQueryString = (params) => {
  const cleaned = Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => [key, String(value)]);
  
  return new URLSearchParams(cleaned).toString();
};